import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertStatus,
  AlertTitle,
  Button,
  ButtonGroup,
  Checkbox,
  Flex,
  Grid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  UseDisclosureReturn,
  useToken,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { DataTable } from '@pelicargo/datatable';
import { AccountType, Nullable, RequireOnly } from '@pelicargo/types';
import { Show, useError } from '@pelicargo/ui';
import { freightServiceOptions, TRUCKING_PROVIDERS, truckingProviderOptions } from '@pelicargo/utils';
import { CargoType, FreightService } from '@prisma/client';
import { createColumnHelper } from '@tanstack/react-table';
import { format, isPast } from 'date-fns';
import { orderBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { HiMiniExclamationCircle } from 'react-icons/hi2';
import { z } from 'zod';

import { trpc, TrpcInputs } from '../config/trpc';
import { Request } from '../utils/types';
import { AirlineInput } from './Autocomplete/AirlineInput';
import { NearbyAirportInput } from './Autocomplete/NearbyAirportInput';
import { SimpleCheckbox } from './SimpleCheckbox';
import { SimpleForm } from './SimpleForm';
import { SimpleReactSelect } from './SimpleReactSelect';
import { SimpleSwitch } from './SimpleSwitch';

type Props = UseDisclosureReturn & {
  request: RequireOnly<Request, 'id' | 'author'>;
  initialOriginAirportId: string;
  initialDestinationAirportId: string;
  freightService?: FreightService;
};

type CreateQuotesForAirlineInput = Exclude<TrpcInputs['createQuotesForAirline'], void>;
type FormValues = CreateQuotesForAirlineInput & { should_create_more: boolean };

const validationSchema = z.object({
  request_id: z.coerce.number(),
  provider_id: z.string(),
  origin_airport_id: z.string(),
  destination_airport_id: z.string(),
  fulfillment_partner_id: z.coerce.number().optional(),
  skip_fulfillment: z.boolean().default(false),
  prefer_direct_fulfillment: z.boolean().optional().default(true),
  freight_service: z.nativeEnum(FreightService).optional(), // This is only used for the createQuoteByRateId flow, but it's easier to include it here than to have a separate form for that flow
  integration_provider_ids: z.array(z.string()).optional().default([]),
  selected_trucking_providers: z.array(z.enum(TRUCKING_PROVIDERS)).optional().default([]),
});

const rateTypeOrder = [
  CargoType.GeneralCargo,
  CargoType.DangerousGoods,
  CargoType.Perishable,
  CargoType.Pharma,
  CargoType.Valuable,
] satisfies CargoType[];

type TableColumns = Nullable<{
  id: number;
  route: string;
  aircraft_types: string;
  cargo_type: string;
  airline_product: string;
  expires_at: Date;
}>;

type TruckingIntegrationProviderId = (typeof TRUCKING_PROVIDERS)[number];

const truckingIntegrationProviderOptions = truckingProviderOptions.map((option) => ({
  label: `${option.label} (Trucking Integration)`,
  value: String(option.value),
}));
const truckingIntegrationProviderIds = [...TRUCKING_PROVIDERS] satisfies TruckingIntegrationProviderId[];
const isTruckingIntegrationProviderId = (value: string): value is TruckingIntegrationProviderId =>
  truckingIntegrationProviderIds.includes(value as TruckingIntegrationProviderId);

export const CreateQuoteModal = ({
  request,
  initialOriginAirportId,
  initialDestinationAirportId,
  onClose,
  freightService,
  ...rest
}: Props) => {
  const { handleError } = useError();
  const { data: account } = trpc.getAccount.useQuery(
    { id: request?.account_id },
    { refetchOnWindowFocus: false, enabled: !!request?.account_id },
  );
  const { mutateAsync: createQuotesForAirline, isLoading: isCreatingQuotesForAirline } =
    trpc.createQuotesForAirline.useMutation();
  const { mutateAsync: createQuoteByRateId, isLoading: isCreatingQuoteByRateId } =
    trpc.createQuoteByRateId.useMutation();
  const isCreating = isCreatingQuotesForAirline || isCreatingQuoteByRateId;

  const [isUnconstrained, setIsUnconstrained] = useState(true);
  const [isCreatingViaIntegration, setIsCreatingViaIntegration] = useState(false);
  const [selectedRateId, setSelectedRateId] = useState<number>();
  const columnHelper = createColumnHelper<TableColumns>();

  const requestId = request.id;
  const methods = useForm<FormValues>({
    defaultValues: {
      request_id: requestId,
      origin_airport_id: initialOriginAirportId,
      destination_airport_id: initialDestinationAirportId,
      provider_id: 'ALL',
      should_create_more: true,
      prefer_direct_fulfillment: true,
      freight_service: freightService,
      fulfillment_partner_id: request.required_fulfillment_partner_id,
      selected_trucking_providers: [],
    },
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
  });

  const providerIdField = methods.watch('provider_id');
  const destinationAirportIdField = methods.watch('destination_airport_id');
  const originAirportIdField = methods.watch('origin_airport_id');
  const fulfillmentPartnerIdField = methods.watch('fulfillment_partner_id');
  const isTruckingIntegrationProviderSelected = isTruckingIntegrationProviderId(providerIdField);

  const { data: provider } = trpc.getProvider.useQuery({ id: providerIdField }, { enabled: !!providerIdField });

  const providerFreightServiceOptions = useMemo(
    () =>
      freightServiceOptions.filter((option) => {
        const service = option.value as FreightService;
        return provider?.provided_services?.some((providedService) => providedService === service);
      }),
    [provider?.provided_services],
  );

  useEffect(() => {
    if (providerFreightServiceOptions.length !== 1) return;

    methods.setValue('freight_service', providerFreightServiceOptions[0].value as FreightService);
  }, [providerFreightServiceOptions, methods]);

  useEffect(() => {
    if (selectedRateId) setSelectedRateId(undefined);

    // Intentionally not including selectedRateId here so it doesn't trigger the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerIdField, originAirportIdField, destinationAirportIdField]);

  const shouldSkipFulfillment = methods.watch('skip_fulfillment');
  const isInlandService = freightService === FreightService.EXW || freightService === FreightService.FCA;

  // Separate from providerListData: integration eligibility is always AFR + has_api_quoting,
  // independent of isInlandService, so it can't share that query's service-scoped filter.
  const { data: integrationProviderListData } = trpc.getProviders.useQuery(
    {
      orderBy: { name: 'asc' },
      filters: {
        has_api_quoting: { equals: true },
        provided_services: { hasSome: [FreightService.AFR] },
        id: { notIn: [...TRUCKING_PROVIDERS] },
      },
    },
    { refetchOnWindowFocus: false },
  );
  const integrationProviderOptions = useMemo(
    () =>
      (integrationProviderListData?.[1] ?? []).map((provider) => ({
        label: `${provider.name} Integration (${provider.id})`,
        value: provider.id,
      })),
    [integrationProviderListData],
  );
  const apiQuotingProviderIds = useMemo(
    () => integrationProviderOptions.map((option) => option.value),
    [integrationProviderOptions],
  );
  const isIntegrationProviderSelected = apiQuotingProviderIds.includes(providerIdField);
  const availableProviderIntegrationOptions = isInlandService
    ? truckingIntegrationProviderOptions
    : integrationProviderOptions;

  const { data: allFPs } = trpc.getAllFulfillmentPartners.useQuery(null);

  const handleCreateBlank = () => {
    methods.setValue('skip_fulfillment', true);
    methods.handleSubmit(onValid)();
  };

  useEffect(() => {
    setIsUnconstrained(providerIdField === 'ALL');
  }, [providerIdField, setIsUnconstrained]);

  // Allows the user to change value part way through creation
  const attemptToCloseModal = useCallback(() => {
    // Make sure we're getting the most recent value
    const shouldCreateMore = methods.getValues('should_create_more');

    if (shouldCreateMore) return;
    onClose?.();
  }, [methods, onClose]);

  const onValid = useCallback(
    async (values: FormValues) => {
      try {
        if (!shouldSkipFulfillment && selectedRateId) {
          await createQuoteByRateId({
            rate_id: selectedRateId,
            request_id: requestId,
            fulfillment_partner_id: values?.fulfillment_partner_id,
            freight_service: values?.freight_service,
          });
          attemptToCloseModal();
          return;
        }

        const selectedProviderAsTrucking = isTruckingIntegrationProviderId(values.provider_id)
          ? values.provider_id
          : undefined;

        if (values.via_integration) {
          const hasAirlineIntegration = Boolean(
            values.provider_id && apiQuotingProviderIds.includes(values.provider_id),
          );
          const hasTruckingIntegration = Boolean(
            selectedProviderAsTrucking || values.selected_trucking_providers?.length,
          );
          if (!hasAirlineIntegration && !hasTruckingIntegration) {
            throw new Error(
              'Select an integration provider in Provider or at least one Trucking Provider before creating via integration.',
            );
          }
          setIsCreatingViaIntegration(true);
        }

        const hasAirlineIntegration = Boolean(values.provider_id && apiQuotingProviderIds.includes(values.provider_id));
        const selectedTruckingProviders = (
          values.via_integration && selectedProviderAsTrucking ? [selectedProviderAsTrucking] : []
        ) as CreateQuotesForAirlineInput['selected_trucking_providers'];

        const payload: CreateQuotesForAirlineInput = {
          request_id: values.request_id,
          provider_id: values.provider_id,
          origin_airport_id: values.origin_airport_id,
          destination_airport_id: values.destination_airport_id,
          fulfillment_partner_id: values.fulfillment_partner_id,
          skip_fulfillment: values.skip_fulfillment,
          prefer_direct_fulfillment: values.prefer_direct_fulfillment,
          freight_service: values.freight_service,
          via_integration: values.via_integration,
          integration_provider_ids: values.via_integration && hasAirlineIntegration ? [values.provider_id] : [],
          selected_trucking_providers: selectedTruckingProviders,
        };
        await createQuotesForAirline(payload);
        setIsCreatingViaIntegration(false);
        attemptToCloseModal();
      } catch (error) {
        handleError(error);
      }
    },
    [
      shouldSkipFulfillment,
      selectedRateId,
      createQuotesForAirline,
      attemptToCloseModal,
      createQuoteByRateId,
      requestId,
      handleError,
      apiQuotingProviderIds,
    ],
  );

  const [red400] = useToken('colors', ['red.400']);

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: '',
        size: 1,
        cell: (info) => <Checkbox value={info.getValue()} isChecked={info.getValue() === selectedRateId} />,
      }),
      columnHelper.accessor('route', {
        header: 'Full Lane',
        sortingFn: 'alphanumeric',
      }),

      columnHelper.accessor('aircraft_types', {
        header: 'AC Types',
      }),
      columnHelper.accessor('cargo_type', {
        header: 'Cargo Type',
        cell: (info) => info.getValue() ?? '-',
      }),
      columnHelper.accessor('airline_product', {
        header: 'Airline Product',
        cell: (info) => info.getValue() ?? '-',
      }),
      columnHelper.accessor('expires_at', {
        header: 'Expiration',
        cell: (info) => {
          const value = info.getValue();
          if (!value) return '-';

          const date = new Date(info.getValue());
          const isOld = isPast(date);
          return (
            <Flex alignItems="center" gap="0.5em">
              <HiMiniExclamationCircle color={isOld ? red400 : ''} opacity={isOld ? 1 : 0} />
              {format(date, 'PP') ?? '-'}
            </Flex>
          );
        },
      }),
    ],
    [selectedRateId, columnHelper, red400],
  );

  const { data: results, isLoading } = trpc.getRatesByConstraints.useQuery(
    {
      provider_id: providerIdField,
      destination_airport_id: destinationAirportIdField,
      origin_airport_id: originAirportIdField,
      request_id: requestId,
      fulfillment_partner_id: fulfillmentPartnerIdField,
    },
    { refetchOnWindowFocus: false },
  );
  const alert = useMemo(() => results?.alert, [results?.alert]);

  const rates = useMemo(() => results?.routes?.flatMap((route) => route.rates), [results?.routes]);

  const data = useMemo(() => {
    const data = results?.routes?.flatMap((route) => {
      const aircraft_types = route?.possible_flight_segments?.map((segment) => segment.aircraft_type)?.join(', ');
      return route.rates.flatMap((rate) => ({
        id: rate.id,
        route: route.path,
        aircraft_types,
        cargo_type: rate.cargo_type,
        airline_product: rate.airline_product,
        expires_at: rate.expires_at,
      }));
    });
    return orderBy(
      data,
      ['route', 'expires_at', (thing) => rateTypeOrder.indexOf(thing.cargo_type)],
      ['asc', 'desc', 'asc'],
    );
  }, [results?.routes]);

  const isActiveRatesFound = useMemo(() => {
    return results?.routes?.some((route) =>
      route?.rates?.some((rate) => {
        const now = new Date();
        const expiresAt = new Date(rate?.expires_at);
        const startsAt = new Date(rate?.starts_at);
        return (!startsAt || now >= startsAt) && (!expiresAt || now <= expiresAt);
      }),
    );
  }, [results?.routes]);

  const isRatesFound = useMemo(() => {
    return results?.routes?.some((route) => !!route?.rates?.length);
  }, [results?.routes]);

  const isRatesConstrained = results?.withConstraints;

  const isIntegrationSelected = isIntegrationProviderSelected || isTruckingIntegrationProviderSelected;

  const isSelectRateVisible = useMemo(() => {
    return isRatesFound && !isLoading && (!isRatesConstrained || !isActiveRatesFound) && !isUnconstrained;
  }, [isUnconstrained, isLoading, isRatesConstrained, isActiveRatesFound, isRatesFound]);

  const isCreateDisabled = useMemo(() => {
    return isCreating || isLoading || (isSelectRateVisible && !selectedRateId) || (!isRatesFound && isUnconstrained);
  }, [isCreating, isLoading, isRatesFound, isSelectRateVisible, isUnconstrained, selectedRateId]);

  const nothingFound = useMemo(() => {
    return !isUnconstrained && !isSelectRateVisible && !isActiveRatesFound && !isLoading;
  }, [isUnconstrained, isSelectRateVisible, isActiveRatesFound, isLoading]);

  const isFPSelectDisplaying = useMemo(() => {
    const TYPES_NEEDING_FP: AccountType[] = [AccountType.IAC, AccountType.UNCERTIFIED];
    const needsFP = TYPES_NEEDING_FP.includes(account?.type);

    return needsFP;
  }, [account?.type]);

  return (
    <SimpleForm submitOnBlur={false} {...methods}>
      <Modal {...rest} onClose={onClose} size="5xl">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton onClick={onClose} />
          <ModalHeader fontSize="lg">New Quote</ModalHeader>
          <ModalBody py={0}>
            <Stack spacing="4" maxH="2xl">
              <Grid templateColumns="repeat(auto-fit, minmax(0, 1fr))" gap={4}>
                <AirlineInput
                  name="provider_id"
                  label="Provider"
                  tabIndex={0}
                  includeAllOption
                  additionalOptions={availableProviderIntegrationOptions}
                  isRequired
                />
                <Show if={providerIdField !== 'ALL' && !isTruckingIntegrationProviderSelected}>
                  <SimpleReactSelect
                    name="freight_service"
                    label="Freight Service"
                    isSearchable
                    options={providerFreightServiceOptions}
                  />
                </Show>
                <NearbyAirportInput
                  name="origin_airport_id"
                  currentAirportId={originAirportIdField}
                  label="Origin"
                  setValue={methods.setValue}
                />
                <Show if={!!destinationAirportIdField}>
                  <NearbyAirportInput
                    name="destination_airport_id"
                    currentAirportId={destinationAirportIdField}
                    label="Destination"
                    setValue={methods.setValue}
                  />
                </Show>
                <Show if={isFPSelectDisplaying}>
                  <SimpleReactSelect
                    name="fulfillment_partner_id"
                    label="Fulfillment Partner"
                    isSearchable
                    options={allFPs?.map((account) => ({
                      label: account?.name,
                      value: String(account?.id),
                    }))}
                  />
                </Show>
              </Grid>
              <Show if={isFPSelectDisplaying}>
                <Stack w="full" align="end">
                  <SimpleCheckbox name="prefer_direct_fulfillment" label="Prefer direct fulfillment" w="fit-content" />
                </Stack>
              </Show>
              <Show if={!!alert && !isIntegrationSelected}>
                <Alert status={alert?.type as AlertStatus}>
                  <AlertIcon />
                  <Flex flexDir="column">
                    <AlertTitle>{alert?.title}</AlertTitle>
                    <AlertDescription>{alert?.message}</AlertDescription>
                  </Flex>
                </Alert>
              </Show>
              <Show if={isLoading && !isIntegrationSelected}>
                <Alert status="info">
                  <AlertIcon />
                  <Flex flexDir="column">
                    <AlertTitle>Loading...</AlertTitle>
                    <AlertDescription>Fetching rates for the selected route</AlertDescription>
                  </Flex>
                </Alert>
              </Show>
              <Show if={isSelectRateVisible}>
                <DataTable
                  data={data ?? []}
                  columns={columns}
                  onRowClick={(row) => {
                    if (selectedRateId === row.id) return setSelectedRateId(undefined);
                    setSelectedRateId(row.id);
                  }}
                  dataCount={rates?.length ?? 0}
                  pagination={{
                    pageIndex: 0,
                    pageSize: rates?.length ?? 0,
                  }}
                  setPagination={() => true}
                  hidePagination
                />
              </Show>
            </Stack>
          </ModalBody>
          <ModalFooter justifyContent="flex-end">
            <ButtonGroup>
              <SimpleSwitch
                name="should_create_more"
                label="Create more"
                width="fit-content"
                display="flex"
                alignItems="center"
                switchProps={{ display: 'flex', alignItems: 'center' }}
              />
              <Button onClick={onClose} colorScheme="gray">
                Close
              </Button>
              <Show if={isIntegrationSelected}>
                <Button
                  colorScheme="blue"
                  onClick={methods.handleSubmit((values) => onValid({ ...values, via_integration: true }))}
                  isLoading={!shouldSkipFulfillment && isCreatingViaIntegration}
                  isDisabled={isCreating || isLoading}
                >
                  Create via Integration
                </Button>
              </Show>
              <Show if={!nothingFound}>
                <Button
                  colorScheme="primary"
                  onClick={methods.handleSubmit(onValid)}
                  isLoading={!shouldSkipFulfillment && isCreating && !isCreatingViaIntegration}
                  isDisabled={isCreateDisabled}
                >
                  Create Quote
                </Button>
              </Show>
              <Show if={nothingFound}>
                <Button
                  onClick={handleCreateBlank}
                  isLoading={shouldSkipFulfillment && isCreating && !isCreatingViaIntegration}
                >
                  Blank
                </Button>
              </Show>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </SimpleForm>
  );
};
